import { ScrollArea } from '@/components/ui/scroll-area'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { useRunEvents } from '@/api/runs'
import { ExecutionEventTimeline } from './ExecutionEventTimeline'
import { cn } from '@/lib/utils'

export interface RunLogsDrawerProps {
  /** The run ID to fetch events for */
  runId: string
  /** Whether the drawer is open */
  open: boolean
  /** Callback when open state changes */
  onOpenChange: (open: boolean) => void
  /** Additional CSS classes */
  className?: string
}

function RawLogsView({ events }: { events: { event_type: string; timestamp: string; metadata: Record<string, unknown> }[] }) {
  return (
    <div data-testid="raw-logs-content" className="h-full">
      <ScrollArea className="h-[calc(100vh-200px)]">
        <pre className="text-xs font-mono bg-muted p-4 rounded-lg overflow-x-auto whitespace-pre-wrap">
          {JSON.stringify(events, null, 2)}
        </pre>
      </ScrollArea>
    </div>
  )
}

/**
 * A drawer component that displays run execution logs and events.
 * Features two tabs: Timeline view (visual) and Raw Logs view (JSON).
 * 
 * @example
 * ```tsx
 * const [open, setOpen] = useState(false)
 * 
 * <RunLogsDrawer
 *   runId="run_123"
 *   open={open}
 *   onOpenChange={setOpen}
 * />
 * ```
 */
export function RunLogsDrawer({
  runId,
  open,
  onOpenChange,
  className,
}: RunLogsDrawerProps) {
  const { data, isLoading, error } = useRunEvents(runId, open)

  const events = data?.events ?? []
  const errorMessage = error instanceof Error ? error.message : error ? String(error) : null

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className={cn('w-full sm:max-w-lg md:max-w-xl lg:max-w-2xl', className)}
      >
        <SheetHeader>
          <SheetTitle>Execution Logs</SheetTitle>
          <SheetDescription>
            Events for run {runId}
          </SheetDescription>
        </SheetHeader>

        <div className="mt-6 h-full">
          <Tabs defaultValue="timeline" className="h-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="timeline">Timeline</TabsTrigger>
              <TabsTrigger value="raw">Raw Logs</TabsTrigger>
            </TabsList>

            <TabsContent value="timeline" className="mt-4">
              <ScrollArea className="h-[calc(100vh-250px)]">
                <ExecutionEventTimeline
                  events={events}
                  isLoading={isLoading}
                  error={errorMessage ?? undefined}
                />
              </ScrollArea>
            </TabsContent>

            <TabsContent value="raw" className="mt-4">
              {isLoading ? (
                <div className="flex items-center justify-center py-8">
                  <span className="text-muted-foreground">Loading...</span>
                </div>
              ) : errorMessage ? (
                <div className="text-red-500">{errorMessage}</div>
              ) : events.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  No events recorded.
                </div>
              ) : (
                <RawLogsView events={events} />
              )}
            </TabsContent>
          </Tabs>
        </div>
      </SheetContent>
    </Sheet>
  )
}
